import React, { FC } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { usePatchObservationForm } from "../../hooks";

interface Props {
	questionsLength: number;
	isDisabled?: boolean;
}

const ObservationPassRate: FC<Props> = ({ questionsLength, isDisabled }) => {
	const { control, trigger, setValue } = useFormContext<AdminObservationForm>();

	const { observationFormId } = useWatch({
		control
	});

	const { mutate: submitScoreThreshold, isLoading: isUpdatingScoreThreshold } =
		usePatchObservationForm();

	const { field } = useController({
		control,
		name: "observationScoreThreshold",
		rules: { required: true }
	});

	const handleSubmitScoreThreshold = async () => {
		if (!observationFormId) return;

		// react-hook-form stores <input type="number"> values as strings
		// by default (onChange receives event.target.value which is a
		// string). The declared type is `number`, but at runtime the
		// value can still be a string — cast to unknown, branch on the
		// runtime type, then coerce to Number so we always send a proper
		// JSON number over the wire. The backend now accepts numeric
		// strings too as a defensive measure, but this keeps the payload
		// correct.
		const rawValue = field.value as unknown;

		// Treat empty/whitespace-only strings as "no value" — Number("")
		// would coerce to 0 and then get clamped to 1, which is not what
		// we want on a cleared input. The field is required, the form's
		// validation layer surfaces this at the UI level.
		if (rawValue === undefined || rawValue === null) return;
		if (typeof rawValue === "string" && rawValue.trim() === "") return;

		const scoreThreshold =
			typeof rawValue === "string" ? Number(rawValue) : (rawValue as number);

		if (!Number.isFinite(scoreThreshold)) return;

		let computedScoreThreshold = scoreThreshold;

		if (scoreThreshold > questionsLength) {
			computedScoreThreshold = questionsLength;
		} else if (scoreThreshold < 1) {
			computedScoreThreshold = 1;
		}

		submitScoreThreshold({
			id: observationFormId,
			score_threshold: computedScoreThreshold
		});
	};

	const handleFocus = () => {
		trigger();
		setValue("selectedQuestionIndex", null);
	};

	const isSingleQuestion = questionsLength === 1;

	return (
		<>
			<div
				className={`border border-border bg-accent rounded-lg ${isDisabled ? "opacity-60 pointer-events-none" : ""}`}
			>
				<div className="flex px-4 py-5 justify-between items-center">
					<div>
						<label className="block font-bold">Observation pass rate</label>
						<label className="block mb-0">
							This form will be scored out of {questionsLength}{" "}
							{isSingleQuestion ? "point" : "points"}, what would you like to
							set the pass rate at?
						</label>
					</div>

					<div className="flex items-center gap-2">
						<input
							type="number"
							min={1}
							max={questionsLength}
							className="bg-white border-0 w-fit max-w-[55px] font-bold p-0 text-center text-lg focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
							{...field}
							value={field.value ?? ""}
							disabled={isUpdatingScoreThreshold || isDisabled}
							onBlur={handleSubmitScoreThreshold}
							onFocus={handleFocus}
							aria-label="score-threshold-input"
						/>

						<span className="font-bold text-lg opacity-[inherit]">
							/ {questionsLength}
						</span>
					</div>
				</div>
			</div>
		</>
	);
};

export default ObservationPassRate;
